import { useLanguage, t } from "../../../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Button, Badge, Spinner } from "react-bootstrap";
import { HiOutlinePencil, HiOutlineUserCircle, HiOutlineShieldCheck } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import Header from "../../../components/layout/Header/Header";
import MobileOffcanvasNav from "../../../components/layout/MobileOffcanvasNav/MobileOffcanvasNav";
import ProfileInfoCard from "../../../components/common/ProfileInfoCard/ProfileInfoCard";
import VerificationNoticeBanner from "../../../components/common/VerificationNoticeBanner/VerificationNoticeBanner";
import ROUTES from "../../../config/routes";
import "./Profile.css";
import Footer from "../../../components/layout/Footer/Footer";
import getCompleteUserProfile from "../../../features/profile/services/getCompleteUserProfile";
import { useAuth } from "../../../features/auth/context/AuthContext";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
function formatGender(value) {
  if (!value) return "-";
  const normalized = String(value).toLowerCase();
  if (normalized === "male") return "Male";
  if (normalized === "female") return "Female";
  return value;
}
function formatYesNo(value) {
  if (!value) return "-";
  const normalized = String(value).toLowerCase();
  if (normalized === "yes") return "Yes";
  if (normalized === "no") return "No";
  return value;
}
function formatRoomTypeValue(value) {
  if (!value) return "-";
  const normalized = String(value).trim().toLowerCase();
  const map = {
    bedspace: "Bedspace",
    "shared room": "Shared Room",
    "private room": "Private Room",
    "studio room": "Studio Room",
    "partition room": "Partition Room"
  };
  return map[normalized] || value;
}
function formatHouseTypeValue(value) {
  if (!value) return "-";
  const normalized = String(value).trim().toLowerCase();
  const map = {
    apartment: "Apartment",
    villa: "Villa",
    "studio apartment": "Studio Apartment",
    compound: "Compound",
    "standalone house": "Standalone House",
    "labour camp": "Labour Camp"
  };
  return map[normalized] || value;
}
function formatGenderPreference(value) {
  if (!value) return "-";
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "male") return "Male Only";
  if (normalized === "female") return "Female Only";
  if (normalized === "male only") return "Male Only";
  if (normalized === "female only") return "Female Only";
  return value;
}
function formatArrayValues(value, formatter) {
  if (!Array.isArray(value)) return formatter ? formatter(value) : value || "-";
  if (!value.length) return "-";
  return value.map(item => formatter ? formatter(item) : item).join(", ");
}
function normalizeProfileResponse(apiData = {}, fallbackUser = {}) {
  const profile = apiData?.profile || {};
  const preferences = apiData?.preferences || {};
  const preferredCitiesText = Array.isArray(preferences?.preferred_cities) ? preferences.preferred_cities.map(item => item?.name).filter(Boolean).join(", ") : "-";
  const preferredNeighbourhoodsText = Array.isArray(preferences?.preferred_neighbourhoods) ? preferences.preferred_neighbourhoods.map(item => item?.name).filter(Boolean).join(", ") : "-";
  const budgetMin = preferences?.budget_min;
  const budgetMax = preferences?.budget_max;
  const formattedBudget = budgetMin || budgetMax ? `SAR ${budgetMin || 0} - SAR ${budgetMax || 0}` : "-";
  const roomTypePreference = Array.isArray(preferences?.room_type_preference) ? preferences.room_type_preference.map(item => formatRoomTypeValue(item)) : [];
  const houseTypePreference = Array.isArray(preferences?.house_type_preference) ? preferences.house_type_preference.map(item => formatHouseTypeValue(item)) : [];
  return {
    ...fallbackUser,
    ...apiData,
    name: apiData?.full_name || fallbackUser?.name || "",
    full_name: apiData?.full_name || fallbackUser?.full_name || "",
    email: apiData?.email || fallbackUser?.email || "",
    contactNumber: apiData?.mobile || fallbackUser?.contactNumber || "",
    mobile: apiData?.mobile || fallbackUser?.mobile || "",
    whatsappNumber: profile?.whatsapp_number || fallbackUser?.whatsappNumber || "",
    age: profile?.age ?? fallbackUser?.age ?? "",
    gender: formatGender(profile?.gender || fallbackUser?.gender || ""),
    occupation: profile?.occupation || fallbackUser?.occupation || "",
    nationality: profile?.nationality?.name || fallbackUser?.nationality || "",
    city: profile?.city?.name || fallbackUser?.city || "",
    district: profile?.neighbourhood?.name || fallbackUser?.district || "",
    aboutMe: profile?.about_me || fallbackUser?.aboutMe || "",
    preferredLocation: preferredCitiesText !== "-" ? `${preferredCitiesText}${preferredNeighbourhoodsText !== "-" ? ` | ${preferredNeighbourhoodsText}` : ""}` : "-",
    budget: formattedBudget,
    roomTypePreference,
    houseTypePreference,
    furnishedPreference: formatYesNo(preferences?.furnished_preference || fallbackUser?.furnishedPreference || ""),
    genderPreference: formatGenderPreference(preferences?.gender_preference || fallbackUser?.genderPreference || ""),
    profile_status: apiData?.profile_status || fallbackUser?.profile_status || "",
    nafath_status: apiData?.nafath_status || fallbackUser?.nafath_status || ""
  };
}
function normalizeLayoutUser(sourceUser = {}) {
  return {
    id: sourceUser?.user_id || sourceUser?.id || "",
    user_id: sourceUser?.user_id || sourceUser?.id || "",
    name: sourceUser?.name || sourceUser?.full_name || "BaytBuddy User",
    full_name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    email: sourceUser?.email || "",
    mobile: sourceUser?.mobile || "",
    profile_status: sourceUser?.profile_status || "",
    nafath_status: sourceUser?.nafath_status || ""
  };
}
export default function Profile() {
  useLanguage();
  useDocumentTitle("My Profile");
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const navigate = useNavigate();
  const {
    user: authUser,
    setUser: setAuthUser
  } = useAuth();
  const layoutUser = useMemo(() => normalizeLayoutUser(authUser || {}), [authUser]);
  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const response = await getCompleteUserProfile();
        if (!isMounted) return;
        if (response?.status && response?.data) {
          const normalizedUser = normalizeProfileResponse(response.data, authUser || {});
          setProfileUser(normalizedUser);
          setAuthUser(prev => ({
            ...(prev || {}),
            user_id: response.data?.user_id || prev?.user_id || "",
            id: response.data?.user_id || prev?.id || "",
            name: response.data?.full_name || prev?.name || "",
            full_name: response.data?.full_name || prev?.full_name || "",
            email: response.data?.email || prev?.email || "",
            mobile: response.data?.mobile || prev?.mobile || "",
            profile_status: response.data?.profile_status || prev?.profile_status || "",
            nafath_status: response.data?.nafath_status || prev?.nafath_status || ""
          }));
          return;
        }
        setProfileUser(normalizeProfileResponse(authUser || {}, authUser || {}));
      } catch (error) {
        setProfileUser(normalizeProfileResponse(authUser || {}, authUser || {}));
        console.error("Get complete user profile error:", error);
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false);
        }
      }
    };
    fetchProfile();
    return () => {
      isMounted = false;
    };
  }, [setAuthUser]);
  const user = profileUser || normalizeProfileResponse(authUser || {}, authUser || {});
  const isProfileComplete = user?.profile_status === "complete";
  const isNafathVerified = user?.nafath_status === "verified";
  const roomTypePreferenceValue = formatArrayValues(user.roomTypePreference, formatRoomTypeValue);
  const houseTypePreferenceValue = formatArrayValues(user.houseTypePreference, formatHouseTypeValue);
  const personalInfo = useMemo(() => [{
    label: "Full Name",
    value: user.name || "-"
  }, {
    label: "Email Address",
    value: user.email || "-"
  }, {
    label: "Contact Number",
    value: user.contactNumber || "-"
  }, {
    label: "WhatsApp Number",
    value: user.whatsappNumber || "-"
  }, {
    label: "Age",
    value: user.age || "-"
  }, {
    label: "Gender",
    value: user.gender || "-"
  }, {
    label: "Occupation",
    value: user.occupation || "-"
  }, {
    label: "Nationality",
    value: user.nationality || "-"
  }, {
    label: "City",
    value: user.city || "-"
  }, {
    label: "District",
    value: user.district || "-"
  }], [user]);
  const livingPreferences = useMemo(() => [{
    label: "Preferred Location",
    value: user.preferredLocation || "-"
  }, {
    label: "Budget Range",
    value: user.budget || "-"
  }, {
    label: "Room Type Preference",
    value: roomTypePreferenceValue || "-"
  }, {
    label: "House Type Preference",
    value: houseTypePreferenceValue || "-"
  }, {
    label: "Furnished Preference",
    value: user.furnishedPreference || "-"
  }, {
    label: "Gender Preference",
    value: user.genderPreference || "-"
  }], [user, roomTypePreferenceValue, houseTypePreferenceValue]);
  const handleEditProfile = () => {
    navigate(ROUTES.EDIT_PROFILE);
  };
  return <div className="bb-dashboard-layout">
      <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
        <Sidebar user={layoutUser} />
      </div>

      <div className="bb-dashboard-layout__content">
        <Header onMenuClick={() => setShowMobileNav(true)} />

        <VerificationNoticeBanner user={layoutUser} />

        <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={layoutUser} />


        <main className="bb-profile-main">
          <Container fluid>
            <div className="bb-profile-hero">
              <div>
                <h1 className="bb-profile-hero__title">{t("My Profile")}</h1>
                <p className="bb-profile-hero__subtitle">{t("Review your personal information, living preferences, and verification status.")}

                </p>
              </div>

              <Button className="bb-profile-hero__btn" onClick={handleEditProfile}>
                <HiOutlinePencil className="me-2" />{t("Edit Profile")}

              </Button>
            </div>

            {isLoadingProfile ? <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" role="status" />
              </div> : <Row className="g-4">
                <Col xl={4}>
                  <Card className="bb-profile-summary-card">
                    <Card.Body>
                      <div className="bb-profile-summary-card__icon">
                        <HiOutlineUserCircle />
                      </div>

                      <h3 className="bb-profile-summary-card__name">{user.name || "-"}</h3>
                      <p className="bb-profile-summary-card__email">{user.email || "-"}</p>

                      <div className="bb-profile-summary-card__badges">
                        <Badge bg={isProfileComplete ? "success" : "secondary"}>
                          {t(isProfileComplete ? "Profile Complete" : "Incomplete Profile")}
                        </Badge>

                        {/* <Badge bg={isNafathVerified ? "success" : "warning"}>
                         {isNafathVerified ? "Nafath Verified" : "Nafath Pending"}
                         </Badge> */}
                      </div>

                      <div className="bb-profile-summary-card__about">
                        <div className="bb-profile-summary-card__about-title">
                          <HiOutlineShieldCheck className="me-2" />{t("About Me")}

                      </div>
                        <p>{user.aboutMe || "-"}</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={8}>
                  <div className="bb-profile-sections">
                    <ProfileInfoCard title={t("Personal Information")} items={personalInfo} />
                    <ProfileInfoCard title={t("Living Preferences")} items={livingPreferences} />
                  </div>
                </Col>
              </Row>}
          </Container>
        </main>

        <Footer />
      </div>
    </div>;
}
